/**
 * Cart-drawer health telemetry (TASK-1323, PR-1).
 *
 * PURE INSTRUMENTATION — nothing here changes cart behavior. It exists to make
 * an otherwise-silent failure class (drawer shows empty while the cart holds
 * items; a window-focus refetch clobbers a pending add; a Firestore snapshot
 * error wedges the product list; an add silently fails) visible in GA4 and
 * Sentry so the mechanism-fix PRs (2-4) can be measured against a real baseline.
 *
 * Two channels, mirroring the design doc §4:
 *   - GA4 (`trackCartHealthEvent`): volume/funnel joins, preview-observable.
 *   - Sentry (`captureMessage`, stable fingerprint): engineering alerting.
 *
 * Reused across the drawer (E1), the cart context (E3/E4), and the realtime
 * hook (E2) — hence a shared module rather than inline helpers.
 */
import * as Sentry from '@sentry/nextjs';
import isEqual from 'deep-eql';
import { trackCartHealthEvent } from '@util/analytics';
import type { Cart } from '@util/types/firestore/carts';

/** Stable GA4/Sentry event names. */
export const CART_TELEMETRY_EVENTS = {
  /** E1: drawer renders its empty state while the cart holds content. */
  DRAWER_EMPTY_MISMATCH: 'cart_drawer_empty_mismatch',
  /** E2: `useRealtimeProducts` onSnapshot error. */
  REALTIME_SNAPSHOT_ERROR: 'cart_realtime_snapshot_error',
  /**
   * E3: focus refetch is about to overwrite a locally-dirty cart.
   * PR-1 ships this as DETECT-ONLY (logs; still overwrites). PR-4 flips it to
   * also skip the overwrite, at which point the name becomes
   * `cart_focus_clobber_prevented`.
   */
  FOCUS_CLOBBER: 'cart_focus_clobber',
  /** E4: an add-to-cart attempt failed (fetch error / deleted product / stock cap). */
  ADD_FAILED: 'cart_add_failed',
} as const;

/** Stable Sentry fingerprints (used by the alert rollup — see PR body). */
export const CART_TELEMETRY_FINGERPRINTS = {
  DRAWER_EMPTY: 'cart-drawer-empty',
  FOCUS_CLOBBER: 'cart-focus-clobber',
  ADD_FAILED: 'cart-add-failed',
} as const;

/**
 * Common context block attached to every cart-health event so GA4 rows and
 * Sentry issues can be sliced by auth state, cart size, and realtime health.
 */
export type CartTelemetryCommon = {
  auth: 'guest' | 'user';
  cart_size: number;
  product_map_size: number;
  realtime_status: 'live' | 'loading' | 'error';
  seller_count: number;
  has_over_30_items: boolean;
};

export function buildCartCommonContext(p: {
  auth: 'guest' | 'user';
  cartSize: number;
  productMapSize: number;
  realtimeStatus: 'live' | 'loading' | 'error';
  sellerCount: number;
}): CartTelemetryCommon {
  return {
    auth: p.auth,
    cart_size: p.cartSize,
    product_map_size: p.productMapSize,
    realtime_status: p.realtimeStatus,
    seller_count: p.sellerCount,
    // >30 distinct items trips Firestore's `in`-query cap (M3) — flag it on
    // every event so the wedge population is sliceable pre-fix.
    has_over_30_items: p.cartSize > 30,
  };
}

/**
 * E1 predicate (pure) — the DRAWER-side M1 case only. True when the drawer
 * renders its FULL empty state (`isEmpty`) even though `cart.items` is
 * non-empty: the realtime-snapshot gap where a just-added item's product isn't
 * in `productMap` yet.
 *
 * NOTE: the 2026-07-15 remove-path desync (`items: []` but `product_ids`/
 * `subtotal` stale) is NOT detectable here — the drawer consumes the
 * `calcCart`-derived cart, which recomputes `product_ids` and `subtotal` from
 * `items` on every render, so those fields are always in sync with `items` at
 * the drawer. That desync lives only in the RAW cart state and the persisted
 * Firestore doc; it is detected at the flush site by `detectRawCartDesync`.
 */
export function isDrawerEmptyMismatch(p: {
  isEmpty: boolean;
  itemsLength: number;
}): boolean {
  return p.isEmpty && p.itemsLength > 0;
}

/**
 * E1 (desync variant) predicate (pure). True for the RAW-cart shape that the
 * debounced persist is about to write to Firestore when it is self-contradictory:
 * `items` is empty but `product_ids` and/or `subtotal` are stale non-empty
 * values. This is the exact 2026-07-15 repro shape — produced by
 * `removeFromCart`/`decreaseCartQty`, which return `{ ...prev, items: filtered }`
 * WITHOUT re-running `calcCart`, so the derived fields lag behind `items`.
 * Returns which fields desynced so the event can report them.
 */
export function detectRawCartDesync(p: {
  itemsLength: number;
  productIdsLength: number;
  subtotal: number;
}): { desync: boolean; desyncFields: string[] } {
  if (p.itemsLength !== 0) return { desync: false, desyncFields: [] };
  const desyncFields: string[] = [];
  if (p.productIdsLength > 0) desyncFields.push('product_ids');
  if (p.subtotal > 0) desyncFields.push('subtotal');
  return { desync: desyncFields.length > 0, desyncFields };
}

/**
 * E3 predicate (pure). A window-focus refetch is about to overwrite local
 * state (`willOverwrite`) AND local state is dirty vs its last-persisted
 * snapshot (`hasPendingLocalChanges`) — i.e. the M2 clobber that loses a
 * just-added item. `itemsOnlyLocal` counts distinct items present locally but
 * absent from the incoming server cart. `last_updated` is excluded from the
 * dirty comparison (matches `shouldUpdateFirestore`).
 */
export function detectFocusClobber(p: {
  firestoreCart: Cart;
  localCart: Cart;
  localInitial: Cart | null;
}): { clobber: boolean; itemsOnlyLocal: number } {
  const stripTs = (c: Cart) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { last_updated, ...rest } = c;
    return rest;
  };
  // Strip `last_updated` from the overwrite comparison too: `calcCart` re-stamps
  // it with Date.now() on every recompute, so a raw `!isEqual` would be TRUE on
  // every focus (vacuous). We only want CONTENT-differing overwrites here.
  const willOverwrite = !isEqual(stripTs(p.firestoreCart), stripTs(p.localCart));
  const hasPendingLocalChanges =
    p.localInitial != null &&
    !isEqual(stripTs(p.localCart), stripTs(p.localInitial));
  const clobber = willOverwrite && hasPendingLocalChanges;
  const serverIds = new Set(p.firestoreCart.items.map((i) => i.product_id));
  const itemsOnlyLocal = p.localCart.items.filter(
    (i) => !serverIds.has(i.product_id)
  ).length;
  return { clobber, itemsOnlyLocal };
}

type SentryOptions = {
  fingerprint: string;
  level?: 'info' | 'warning' | 'error';
};

/**
 * Emit a cart-health event to GA4 always, and to Sentry only when `sentry` is
 * provided. Both channels are wrapped so a telemetry failure can never break
 * the cart flow (this is instrumentation — it must be invisible on failure).
 */
export function emitCartTelemetry(
  eventName: string,
  payload: Record<string, unknown>,
  sentry?: SentryOptions
): void {
  try {
    trackCartHealthEvent(eventName, payload);
  } catch {
    // swallow — telemetry must never surface to the user
  }
  if (sentry) {
    try {
      Sentry.captureMessage(eventName, {
        level: sentry.level ?? 'warning',
        fingerprint: [sentry.fingerprint],
        tags: { area: 'cart', cart_event: eventName },
        extra: payload,
      });
    } catch {
      // swallow
    }
  }
}
